import { Component, ChangeDetectionStrategy, ViewEncapsulation, inject, input, computed } from '@angular/core';
import { saveAs } from 'file-saver';
import { ContentListComponent, ContentListItemComponent } from '@workbuddy/ui/components/content-list';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { AIService } from '@workbuddy/ai/core/ai.service';
import type { AICodeExecutionFile } from '@jms/model';


@Component({
  selector: 'agent-file-download-list',
  standalone: true,
  imports: [ContentListComponent, ContentListItemComponent],
  templateUrl: './file.download.list.component.html',
  styleUrls: ['./file.download.list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AgentFileDownloadListComponent {

  public readonly $files = input.required<AICodeExecutionFile[]>({ alias: 'files' });

  private readonly aiService = inject(AIService);
  private readonly toastService = inject(ToastService);

  protected readonly $displayFiles = computed(() => {

    return this.$files().map(file => ({
      fileId: file.fileId,
      filename: file.filename,
      icon: this.getFileIcon(file.filename),
      description: this.getFileDescription(file.filename),
      command: new Command({
        iconLeft: 'fas fa-download',
        tooltip: 'Download',
        disabled: false,
        action: () => this.download(file),
      }),
    }));
  });

  private getFileIcon(filename: string): string {

    const ext = filename.toLowerCase();
    if (/\.(png|jpg|jpeg|gif|webp|svg)$/.test(ext)) return 'fas fa-file-image';
    if (/\.pdf$/.test(ext)) return 'fas fa-file-pdf';
    if (/\.(xlsx|xls|csv)$/.test(ext)) return 'fas fa-file-excel';
    if (/\.(pptx|ppt)$/.test(ext)) return 'fas fa-file-powerpoint';
    if (/\.(docx|doc|txt|md|json|py)$/.test(ext)) return 'fas fa-file-lines';
    return 'fas fa-file';
  }

  private getFileDescription(filename: string): string {

    const ext = filename.toLowerCase();
    if (/\.(png|jpg|jpeg|gif|webp|svg)$/.test(ext)) return 'Image';
    if (/\.pdf$/.test(ext)) return 'PDF';
    if (/\.(xlsx|xls|csv)$/.test(ext)) return 'Spreadsheet';
    if (/\.(pptx|ppt)$/.test(ext)) return 'Presentation';
    if (/\.(docx|doc|txt|md|json|py)$/.test(ext)) return 'Document';
    return 'File';
  }

  private download(file: AICodeExecutionFile): void {

    const cmd = this.$displayFiles().find(f => f.fileId === file.fileId)?.command;
    if (cmd) {
      cmd.iconLeft = 'fas fa-circle-notch fa-spin';
      cmd.tooltip = 'Downloading...';
      cmd.disabled = true;
    }

    this.aiService.downloadFile(file.fileId, file.filename).subscribe({
      next: (blob) => {
        saveAs(blob, file.filename);
        if (cmd) {
          cmd.iconLeft = 'fas fa-check';
          cmd.tooltip = 'Downloaded';
          setTimeout(() => {
            cmd.iconLeft = 'fas fa-download';
            cmd.tooltip = 'Download';
            cmd.disabled = false;
          }, 2000);
        }
      },
      error: () => {
        if (cmd) {
          cmd.iconLeft = 'fas fa-download';
          cmd.tooltip = 'Download';
          cmd.disabled = false;
        }
        this.toastService.error('Download failed', `Could not download ${file.filename}`);
      },
    });
  }
}
